const palette = {
  primary: {
    // Keep 300/600/700 aligned with theme.js primary.light/main/dark.
    50: "#F2FBF8",
    100: "#E1F7F1",
    200: "#BDEBE0",
    300: "#4FD7BA",
    400: "#55CCB2",
    500: "#5BC5AA",
    600: "#62BDA4",
    700: "#14997E",
    800: "#107A65",
    900: "#0B5C4C",
  },
  neutral: {
    50: "#FBFBFC",
    100: "#F6F7FB",
    200: "#EEF1F6",
    300: "#E7EBF2",
    400: "#D5DDE8",
    500: "#B7C0CD",
    600: "#96A1B3",
    700: "#657089",
    800: "#47536B",
    900: "#1F2840",
  },
  success: {
    50: "#ECF8F1",
    100: "#D7F2E2",
    200: "#AFE4C4",
    300: "#7ED5A2",
    400: "#4EC47F",
    500: "#2FAD67",
    600: "#1F9A5A",
    700: "#197C48",
    800: "#145F38",
    900: "#10482B",
  },
  warning: {
    50: "#FFF7EB",
    100: "#FFEDD1",
    200: "#FFD9A3",
    300: "#FFC06E",
    400: "#F2A53E",
    500: "#E69427",
    600: "#DB8A1B",
    700: "#B46E12",
    800: "#8E560E",
    900: "#6C410A",
  },
  error: {
    50: "#FEF2F2",
    100: "#FDE3E2",
    200: "#FBC8C5",
    300: "#F6A39F",
    400: "#EE7872",
    500: "#E15049",
    600: "#C9352E",
    700: "#A8221C",
    800: "#821A16",
    900: "#631411",
  },
  info: {
    50: "#EFF6FF",
    100: "#DBEAFE",
    200: "#BFDBFE",
    300: "#93C5FD",
    400: "#60A5FA",
    500: "#3B82F6",
    600: "#2563EB",
    700: "#1D4ED8",
    800: "#1E40AF",
    900: "#1E3A8A",
  },
};

const absolute = {
  white: "#FFFFFF",
  black: "#000000",
};

const color = {
  brandDefaultLight: palette.primary[300],
  brandDefault: palette.primary[600],
  brandDefaultStrong: palette.primary[700],
  brandMuted: palette.primary[50],
  brandMutedStrong: palette.primary[100],
  brandMutedStrong2: palette.primary[200],
  textPrimary: palette.neutral[900],
  textSecondary: palette.neutral[700],
  textTertiary: palette.neutral[600],
  textInverse: absolute.white,
  textBlack: absolute.black,
  surfaceWhite: absolute.white,
  surfaceDefault: palette.neutral[50],
  surfaceSubtle: "#F8FAFC",
  borderDefault: palette.neutral[300],
  borderStrong: palette.neutral[400],
  borderEmphasis: palette.neutral[500],
  successDefault: palette.success[600],
  successDefaultStrong: palette.success[700],
  successMuted: palette.success[50],
  successMutedStrong: palette.success[100],
  warningDefault: palette.warning[600],
  warningDefaultStrong: palette.warning[700],
  warningMuted: palette.warning[50],
  warningMutedStrong: palette.warning[100],
  errorDefault: palette.error[700],
  errorDefaultStrong: palette.error[800],
  errorMuted: palette.error[50],
  errorMutedStrong: palette.error[100],
  infoDefault: palette.info[600],
  infoDefaultStrong: palette.info[700],
  infoMuted: palette.info[50],
  infoMutedStrong: palette.info[100],
};

const category = {
  "01": { default: "#D64545", muted: "#FDECEC" },
  "02": { default: "#2E9B59", muted: "#E8F5EC" },
  "03": { default: "#3478D4", muted: "#EAF2FC" },
  "04": { default: "#E0B400", muted: "#FFF6CC" },
  "05": { default: "#D94F9D", muted: "#FBEAF4" },
  "06": { default: "#24272B", muted: "#EEF0F2" },
  "07": { default: "#E97B24", muted: "#FDF0E5" },
  "08": { default: "#7B61C9", muted: "#F0ECFA" },
  "09": { default: "#1C9A9A", muted: "#E4F5F4" },
  "10": { default: "#8A6248", muted: "#F1E9E4" },
};

const spacing = {
  2: 2,
  4: 4,
  6: 6,
  8: 8,
  10: 10,
  12: 12,
  14: 14,
  16: 16,
  20: 20,
  24: 24,
  28: 28,
  32: 32,
  40: 40,
  48: 48,
};

const breakpoint = {
  md: 1024,
  lg: 1280,
  xl: 1440,
};

export const dsTokens = {
  fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif',
  absolute,
  palette,
  color,
  category,
  spacing,
  breakpoint,
  spacingRole: {
    fieldGap: spacing[4],
    fieldLayoutRowSm: spacing[8],
    fieldLayoutRowMd: spacing[12],
    fieldLayoutRowLg: spacing[16],
    fieldLayoutColumn: spacing[12],
    choiceDescriptionGap: spacing[4],
    actionGapXs: spacing[6],
    actionGapSm: spacing[8],
    actionGapMd: spacing[12],
    actionGapLg: 14,
    actionGap: spacing[12],
    iconGap: spacing[8],
    controlGap: spacing[12],
    optionGap: spacing[12],
    radioOptionGapInline: spacing[24],
  },
  insetRole: {
    cardInsetInline: spacing[20],
    cardEdgeTop: spacing[20],
    cardEdgeBottom: spacing[20],
    cardZoneGap: spacing[16],
    cardTitleGap: spacing[4],
    controlInsetInline: spacing[12],
    controlInsetInlineCompact: spacing[12],
    controlInsetBlock: spacing[12],
    controlInsetBlockCompact: spacing[10],
    controlAffixInsetStart: spacing[12],
    controlAffixInsetEnd: spacing[10],
    controlAffixContentOffsetStart: spacing[12] + spacing[16] + spacing[8],
    controlAffixContentOffsetEnd: spacing[10] + spacing[20] + spacing[10],
    menuInset: spacing[6],
    menuStack: spacing[2],
    dialogViewportInset: spacing[24],
    dialogViewportInsetCompact: spacing[16],
    dialogInsetInline: spacing[24],
    dialogEdgeTop: spacing[24],
    dialogEdgeBottom: spacing[24],
    dialogZoneGap: spacing[24],
    dialogWidthSm: 480,
    dialogWidthMd: 680,
    dialogWidthLg: 840,
    dialogCloseSize: 32,
    dialogConfirmWidth: 480,
    dialogConfirmHeaderBottom: spacing[20],
    dialogConfirmFooterTop: spacing[24],
    dialogSubtitleIndent: spacing[28],
    tableCellInsetInline: spacing[16],
    appShellContentPadding: spacing[24],
    contentLayoutScrollbarHitWidth: spacing[16],
    contentLayoutScrollbarRailWidth: 6,
    contentLayoutScrollbarThumbMinHeight: spacing[32],
  },
  appShell: {
    topbarHeight: 48,
    sidebarWidthCollapsed: 76,
    sidebarWidthExpanded: 256,
    sidebarInnerWidth: 256 - 20,
    sidebarNavItemWidthCollapsed: 56,
    sidebarNavIconSize: 20,
    sidebarLogoMarkSize: 40,
    sidebarProfileHeight: 54,
    sidebarProfileIconSize: 32,
    topbarActionSize: 30,
    topbarActionIconSize: 20,
    topbarYearSelectWidth: 165,
    topbarBreadcrumbMinWidth: 240,
    topbarBreadcrumbMaxWidth: 420,
    topbarBreadcrumbMaxWidthCompact: 320,
    workspaceGap: spacing[24],
    workspaceContentStackGap: spacing[16],
    workspaceContentInlineGap: spacing[24],
    workspaceHeaderGap: spacing[24],
    workspaceHeaderTextGap: spacing[8],
  },
  table: {
    footerHeight: 32,
  },
  radius: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    pill: 999,
  },
  typography: {
    fontSize12: 12,
    fontSize13: 13,
    fontSize14: 14,
    fontSize16: 16,
    fontSize18: 18,
    fontSize24: 24,
    fontSize32: 32,
    lineHeight16: 16,
    lineHeight18: 18,
    lineHeight20: 20,
    lineHeight24: 24,
    lineHeight28: 28,
    lineHeight32: 32,
    lineHeight36: 36,
    weightRegular: 400,
    weightMedium: 500,
    weightSemibold: 600,
    weightBold: 700,
  },
  typographyRole: {
    heading0: {
      fontSize: 32,
      lineHeight: 36,
      fontWeight: 700,
    },
    heading1: {
      fontSize: 24,
      lineHeight: 28,
      fontWeight: 700,
    },
    heading2: {
      fontSize: 18,
      lineHeight: 24,
      fontWeight: 700,
    },
    heading3: {
      fontSize: 16,
      lineHeight: 24,
      fontWeight: 700,
    },
    heading4: {
      fontSize: 14,
      lineHeight: 20,
      fontWeight: 700,
    },
    label0: {
      fontSize: 16,
      lineHeight: 24,
      fontWeight: 500,
    },
    label1: {
      fontSize: 14,
      lineHeight: 20,
      fontWeight: 500,
    },
    label2: {
      fontSize: 13,
      lineHeight: 18,
      fontWeight: 500,
    },
    label3: {
      fontSize: 12,
      lineHeight: 16,
      fontWeight: 500,
    },
    body0: {
      fontSize: 16,
      lineHeight: 20,
      fontWeight: 400,
    },
    body1: {
      fontSize: 14,
      lineHeight: 20,
      fontWeight: 400,
    },
    body2: {
      fontSize: 13,
      lineHeight: 18,
      fontWeight: 400,
    },
    body3: {
      fontSize: 12,
      lineHeight: 16,
      fontWeight: 400,
    },
  },
  control: {
    heightXs: 28,
    heightSm: 32,
    heightMd: 36,
    heightLg: 40,
  },
  chip: {
    heightSm: 24,
    heightMd: 28,
    heightLg: 36,
    radiusSm: 6,
    radiusMd: 8,
    radiusLg: 10,
    paddingInlineSm: 8,
    paddingInlineMd: 10,
    paddingInlineLg: 12,
    iconGapSm: 4,
    iconGapMd: 6,
    iconGapLg: 6,
    iconSizeSm: 14,
    iconSizeMd: 16,
    iconSizeLg: 18,
  },
  tooltip: {
    bg: palette.neutral[800],
    text: color.textInverse,
    radius: 10,
    paddingInline: spacing[10],
    paddingBlock: spacing[6],
    maxWidth: 248,
    offset: 0,
    arrowSize: spacing[6],
    fontSize: 13,
    lineHeight: 18,
    fontWeight: 400,
    enterDelay: 400,
    enterNextDelay: 120,
    leaveDelay: 0,
    zIndex: 1600,
  },
  shadow: {
    sm: "0 6px 14px rgba(15, 23, 42, 0.06)",
    md: "0 14px 32px rgba(15, 23, 42, 0.08)",
    lg: "0 22px 60px rgba(15, 23, 42, 0.18), 0 10px 24px rgba(15, 23, 42, 0.08)",
  },
  effect: {
    focusBrandSubtle: "0 0 0 3px color-mix(in srgb, var(--ds-color-brand-default) 12%, transparent)",
    focusBrandDefault: "0 0 0 3px color-mix(in srgb, var(--ds-color-brand-default) 16%, transparent)",
    focusError: "0 0 0 3px color-mix(in srgb, var(--ds-color-error-default) 14%, transparent)",
    glowBrandDefault: "0 8px 18px color-mix(in srgb, var(--ds-color-brand-default) 18%, transparent)",
    glowBrandSubtle: "0 6px 14px color-mix(in srgb, var(--ds-color-brand-default) 8%, transparent)",
    glowError: "0 8px 18px color-mix(in srgb, var(--ds-color-error-default) 18%, transparent)",
    chipGloss: "inset 0 1px 0 rgba(255, 255, 255, 0.92), inset 0 -1px 0 rgba(255, 255, 255, 0.22)",
    insetHighlight: "inset 0 1px 0 rgba(255, 255, 255, 0.8)",
    microShadow: "0 1px 2px rgba(17, 24, 39, 0.18)",
  },
  motion: {
    durationFast: 120,
    durationBase: 140,
    durationModerate: 160,
    durationShell: 180,
    easeStandard: "ease",
  },
};

export default dsTokens;
